import { selector } from 'recoil';

import { gameTypeState } from '@/framework/game';

import { boardState } from './boardState';

const WIDTH = 7;
const HEIGHT = 6;
const CONNECT = 4;

const DIRECTIONS: ReadonlyArray<readonly [number, number]> = [
  [0, 1],
  [1, 0],
  [1, 1],
  [1, -1],
];

export type MultisTally = {
  counts: Record<string, number>;
  cells: Record<string, true>;
};

/**
 * Same windows as the server: each length-4 line counts once.
 * A run of 5 counts as 2. Cells in those windows are marked.
 */
export function tallyMultis(
  owners: ReadonlyArray<ReadonlyArray<string | undefined>>,
): MultisTally {
  const counts: Record<string, number> = {};
  const cells: Record<string, true> = {};

  for (let col = 0; col < WIDTH; col += 1) {
    for (let row = 0; row < HEIGHT; row += 1) {
      const pid = owners[col]?.[row];
      if (!pid) continue;

      for (const [dx, dy] of DIRECTIONS) {
        const line: Array<[number, number]> = [];
        let complete = true;

        for (let step = 0; step < CONNECT; step += 1) {
          const x = col + dx * step;
          const y = row + dy * step;
          if (
            x < 0 ||
            x >= WIDTH ||
            y < 0 ||
            y >= HEIGHT ||
            owners[x]?.[y] !== pid
          ) {
            complete = false;
            break;
          }
          line.push([x, y]);
        }

        if (!complete) continue;

        counts[pid] = (counts[pid] ?? 0) + 1;
        for (const [x, y] of line) {
          cells[`${x}:${y}`] = true;
        }
      }
    }
  }

  return { counts, cells };
}

export const multisTallySelector = selector<MultisTally | undefined>({
  key: 'multisTallySelector',
  get: ({ get }) => {
    if (get(gameTypeState) !== 'multis') return undefined;

    const owners: Array<Array<string | undefined>> = [];
    for (let col = 0; col < WIDTH; col += 1) {
      const column = boardState[col];
      owners[col] = [];
      for (let row = 0; row < HEIGHT; row += 1) {
        const cell = column?.[row];
        owners[col][row] = cell ? get(cell)?.pid : undefined;
      }
    }

    return tallyMultis(owners);
  },
});
